import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Checkbox from '../../../App/Components/Checkbox/Checkbox'
import DateRangeCard from '../../../App/Components/DateRangeCard/DateRangeCard'
import { IAction } from '../../../App/Components/DateRangeCard/IProps'
import Spinner from '../../../App/Components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../App/Hooks/reduxHooks'
import { formatDate } from '../../../App/Util'
import {
  getPreferences,
  setPreference,
} from '../../../Preference/Store/actions'
import { useGeAbsenceTypeListQuery } from '../../AbsenceTypeSelect/useAbsenceTypeQuery'
import { usePostGeneralAbsenceMutation } from '../../GeneralAbsence/util/useGeneralAbsenceQuery'
import { handelFormError } from '../../layout/AbsenceFormBase/handelFormError'
import { handelFormSuccess } from '../../layout/AbsenceFormBase/handelFormSuccess'
import { IAbsenceFormBasePayload } from '../../layout/AbsenceFormBase/IAbsenceFormBasePayload'
import { IAbsence } from '../Client/IAbsence'

import { IHoliday } from './Client/IHoliday'
import { getHolidays } from './Store/Actions'

export const HolidayOverview = () => {
  const [absenceTypeUuid, setAbsenceTypeUuid] = useState<string>()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { mutate, isSuccess, error } =
    usePostGeneralAbsenceMutation(queryClient)

  const absenceTypeList = useGeAbsenceTypeListQuery()

  const establishment = useAppSelector(state => state.establishment)
  const preferences = useAppSelector(state => state.preferences)
  const holidays = useAppSelector(state => state.holidays)
  const publicHolidayAbsences = useAppSelector(state =>
    state.absences.list?.filter(
      (absence: IAbsence) =>
        absence.type === 'public_holiday' &&
        absence.personId === null &&
        !absence.isDeleted,
    ),
  )

  const [isChecked, setIsChecked] = useState(false)

  const [isAdding, setIsAdding] = useState<string[]>([])

  const countryCode = establishment.active?.country || 'BE'

  useEffect(() => {
    if (!holidays.isLoading && !holidays.error && !holidays.list) {
      dispatch(getHolidays(countryCode))
    }

    if (establishment && !preferences.list && !preferences.loading) {
      dispatch(getPreferences(establishment.active?.uuid))
    }
  }, [establishment])

  useEffect(() => {
    if (preferences.list) {
      setIsChecked(preferences.list.absencesAutoCreateHolidays)
    }
  }, [preferences])

  useMemo(() => {
    if (absenceTypeList.data && absenceTypeList.data.list) {
      const foundUuid = absenceTypeList.data.list.map(absenceType =>
        absenceType.types.find(
          typeDetails => typeDetails.key === 'public_holiday',
        ),
      )
      setAbsenceTypeUuid(foundUuid[0] ? foundUuid[0].uuid : 'error')
    }
  }, [absenceTypeList.data])

  useMemo(
    () => handelFormError('Could not add holiday.', error, t, dispatch),
    [error, t, dispatch],
  )

  useMemo(() => {
    if (isSuccess) {
      handelFormSuccess(t('Holiday added.'), dispatch)
    }
  }, [isSuccess, t, dispatch])

  if (!holidays || absenceTypeList.status === 'pending') {
    return <Spinner />
  }

  const onAddClicked = (holiday: IHoliday) => {
    if (absenceTypeUuid && absenceTypeUuid !== 'error') {
      setIsAdding([...isAdding, holiday.id])
      const payloadObject: IAbsenceFormBasePayload = {
        starts_at: formatDate(holiday.startsAt, 'FORMAT_FULL_DATE_API'),
        ends_at: formatDate(holiday.endsAt, 'FORMAT_FULL_DATE_API'),
        absence_type_uuid: absenceTypeUuid,
        reason: holiday.translatedName,
      }
      mutate(payloadObject)
    } else {
      return alert(t('Absence type error'))
    }
  }

  const onCheckBoxClick = (checkedValue: boolean) => {
    setIsChecked(checkedValue)
    if (establishment) {
      dispatch(
        setPreference(
          establishment.active?.uuid,
          'absences_auto_create_holidays',
          checkedValue,
        ),
      )
    }
  }

  const isHolidayAdded = (holiday: IHoliday): boolean => {
    if (isAdding.indexOf(holiday.id) > -1) {
      return true
    }

    return (
      publicHolidayAbsences?.some((absence: IAbsence) => {
        return (
          absence.startsAt.isSameOrBefore(holiday.startsAt, 'day') &&
          absence.endsAt.isSameOrAfter(holiday.endsAt, 'day')
        )
      }) || false
    )
  }

  return (
    <div className={'page-layout'}>
      &laquo;&nbsp;
      <Link to={'/hr/general-absences'}>{t('Back to absences')}</Link>
      <h1>{t('Holidays')}</h1>
      {preferences.list ? (
        <div style={{ marginBottom: '1em' }}>
          <Checkbox
            label={t('Automatically add holidays')}
            onChange={onCheckBoxClick}
            isChecked={isChecked}
            help={t(
              'Every week new holidays for the next year will be added. f.e.: on April 30th, 2018 holidays between April 30th, 2019 and May 6th, 2019 will be added.',
            )}
          />
        </div>
      ) : undefined}
      {holidays.list?.map(holiday => {
        const actions: IAction[] = []

        if (!isHolidayAdded(holiday)) {
          actions.push({
            name: 'Add',
            icon: 'add',
            callback: () => {
              onAddClicked(holiday)
            },
          })
        }

        return (
          <DateRangeCard
            key={holiday.id}
            startsAt={holiday.startsAt}
            endsAt={holiday.endsAt}
            title={holiday.translatedName}
            actions={actions}
          />
        )
      })}
    </div>
  )
}
